<template>
    <div v-if="topic || is_private" class="middle-sidebar-content">
        <p class="subtitle middle-sidebar-title is-5 has-text-weight-bold">
            <span v-if="is_private">Private Notes</span>
            <span v-else-if="topic">{{ topic.name }}</span>
        </p><br/>

        <div class="columns is-variable is-1 is-paddingless">
            <div v-if="space_topic_permission('NOTE_CREATE', topic)" class="column">
                <base-button @click="new_note" class="is-block themed-button" icon="fas fa-plus">
                    Create Note
                </base-button>
            </div>

            <div v-if="space_topic_permission('COLLECTION_CREATE', topic)" class="column is-narrow">
                <base-button
                    @click="new_collection"
                    class="is-block themed-button"
                    icon="fas fa-folder-plus"/>
            </div>
        </div>

        <div class="load">
            <span v-show="tree.loading" class="loader"></span>
        </div>

        <div
            class="tree-root"
            @dragover.stop="$event.preventDefault()"
            @drop.prevent="move_to_root">

            <template v-for="item in tree_structure(null)">
                <tree-collection
                    v-if="item.kind === 'collection'"
                    :key="item.kind + item.id"
                    :space="space"
                    :collection="item"
                    :is_private="is_private"
                    :topic="topic"
                    @new-collection="new_collection"
                    @new-note="new_note"
                    @renamed="load"
                    class="item"/>

                <tree-note
                    v-else-if="item.kind === 'note'"
                    :key="item.kind + item.id"
                    :space="space"
                    :note="item"
                    class="item"/>
            </template>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import TreeCollection from './TreeCollection.vue';
import TreeNote from './TreeNote.vue';
import { copy } from '../../utils/helpers';

export default {
    name: 'TreeSidebar',
    components: { TreeNote, TreeCollection },
    props: ['space'],
    computed: {
        ...mapState('tree', ['tree', 'moving_element', 'open_collections', 'open_notes']),
        ...mapGetters('tree', ['tree_structure']),
        ...mapGetters('topics', { get_topic_by_id: 'by_id' }),
        is_private() {
            return 'private' in this.$route.query;
        },
        topic() {
            return this.get_topic_by_id(this.$route.query.topic);
        },
    },
    mounted() {
        this.load();
    },
    destroyed() {
        this.$store.commit('tree/clear_tree');
    },
    watch: {
        $route(new_router, old_route) {
            if (new_router.query.topic !== old_route.query.topic) {
                this.load();
            }
        },
    },
    methods: {
        load() {
            this.load_last_note();

            this.$store.dispatch('tree/list', {
                space_id: this.space.id,
                topic_id: this.$route.query.topic,
                parents: this.open_collections || [],
                is_private: this.is_private,
                root: true,
            });
        },
        load_last_note() {
            const last_opened_note = this.open_notes[this.$route.query.topic];
            if (!this.$route.query.note && last_opened_note) {
                this.$router.replace({
                    name: 'content',
                    params: this.$route.params,
                    query: {
                        ...this.$route.query,
                        note: last_opened_note,
                    },
                });
            }
        },
        async new_collection(parent_id = null) {
            await this.$store.dispatch('tree/create_collection', {
                space_id: this.space.id,
                topic_id: this.$route.query.topic,
                is_private: this.is_private,
                name: 'New Collection',
                parent_id,
            });
        },
        async new_note(collection_id = null) {
            const note = await this.$store.dispatch('notes/create', {
                space_id: this.space.id,
                data: {
                    collection: collection_id,
                    topic: this.$route.query.topic,
                    is_private: this.is_private,
                },
            });

            this.$store.commit('tree/add_item', { data: note, kind: 'note', parent_id: collection_id });
            const query = copy(this.$route.query);
            query.note = note.id;
            query.created = true;
            this.$router.replace({ name: 'content', params: this.$route.params, query });
        },
        move_to_root() {
            const { kind } = this.moving_element;
            if (kind === 'collection' && !this.space_permission('COLLECTION_UPDATE')) return;
            if (kind === 'note' && !this.space_permission('NOTE_UPDATE')) return;

            this.$store.dispatch('tree/move_item', {
                kind,
                id: this.moving_element.id,
                space_id: this.space.id,
                move_to: null,
            });
        },
    },
};
</script>

<style scoped>
    .load {
        position: absolute;
        padding-left: 0.5rem;
    }

    .tree-root {
        margin-top: -2rem;
        padding-top: 2rem;
        padding-bottom: 5rem;
    }
</style>
